import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { CourseService } from './course.service';

import * as CourseActions from './course.actions';
import { catchError, EMPTY, finalize, map, of, switchMap } from 'rxjs';
import { PageLoaderService } from '../../../util/pageloader.service';

@Injectable()
export class CoursesEffects {
  private actions$ = inject(Actions);
  private courseService = inject(CourseService);
  private pageLoader = inject(PageLoaderService);


  fetchAndLoad$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActions.fetchCourseItems.type),
      switchMap((action: any) => {
        this.pageLoader.open();
        return this.courseService.getAll(action.page).pipe(
          map((response) => {
            return CourseActions.fetchCourseItemsSuccess({
              courseResponse: response,
            });
          }),
          catchError((err) =>
            of(CourseActions.fetchCourseItemsFailed({ error: err }))
          ),
          finalize(() => this.pageLoader.close())
        );
      })
    )
  );

  initiateCreateFlow = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActions.initiateCourseCreation.type),
      switchMap(() => {
        this.pageLoader.open();
        return this.courseService.getAllCategories().pipe(
          map((results) =>
            CourseActions.initiateCourseCreationSuccess({ categories: results })
          ),
          finalize(() => this.pageLoader.close())
        );
      })
    )
  );

  create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActions.createCourseItem.type),
      switchMap((action: any) => {
        this.pageLoader.open();
        return this.courseService.create(action.courseItemDTO).pipe(
          map((response) => {
            return CourseActions.createCourseItemSuccess({
              courseItem: response,
            });
          }),
          catchError((err) => {
            return of(CourseActions.createCourseItemFailed({ error: err }));
          }),
          finalize(() => this.pageLoader.close())
        );
      })
    )
  );

  update$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActions.updateCourseItem.type),
      switchMap((action: any) => {
        this.pageLoader.open();
        return this.courseService
          .update(action.courseId, action.courseItemDTO)
          .pipe(
            map((response) => {
              return CourseActions.updateCourseItemSuccess({
                courseItem: response,
              });
            }),
            catchError((err) => {
              return of(CourseActions.updateCourseItemFailed({ error: err }));
            }),
            finalize(() => this.pageLoader.close())
          );
      })
    )
  );

  restoreCreated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActions.restoreCreateCourseItem.type),
      switchMap((action: any) => {
        this.pageLoader.open();
        return this.courseService.getById(action.courseId).pipe(
          map((response) => {
            return CourseActions.restoreCreateCourseItemSuccess({
              courseItem: response,
            });
          }),
          catchError(() => {
            return of();
          }),
          finalize(() => this.pageLoader.close())
        );
      })
    )
  );

  getItem$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CourseActions.selectCourseItem.type),
      switchMap((action: any) => {
        this.pageLoader.open();
        return this.courseService.getById(action.id).pipe(
          map((response) =>
            CourseActions.selectCourseItemSuccess({ item: response })
          ),
          finalize(() => this.pageLoader.close())
        );
      })
    );
  });

  syncCourseContent$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CourseActions.syncCourseContents.type),
      switchMap((action: any) => {
        return this.courseService
          .updateContents(action.courseId, action.courseContents)
          .pipe(
            map((response) =>
              CourseActions.syncCourseContentsSuccess({ courseItem: response })
            )
          );
      })
    );
  });

  fetchEnrolledCourses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActions.fetchEnrollments.type),
      switchMap((action: any) => {
        this.pageLoader.open();
        return this.courseService.getEnrollments().pipe(
          map((response) => {
            return CourseActions.fetchEnrollmentsSuccess({
              enrolledCourses: response,
            });
          }),
          catchError((err) => EMPTY),
          finalize(() => this.pageLoader.close())
        );
      })
    )
  );

  fetchInstructorCourses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActions.fetchAllForInstructor.type),
      switchMap((action: any) => {
        this.pageLoader.open();
        return this.courseService.getAllForInstructor().pipe(
          map((response) => {
            return CourseActions.fetchCourseItemsSuccess({
              courseResponse: response,
            });
          }),
          catchError((err) =>
            of(CourseActions.fetchCourseItemsFailed({ error: err }))
          ),
          finalize(() => this.pageLoader.close())
        );
      })
    )
  );

  sendForReview$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CourseActions.requestForReview.type),
      switchMap((action: any) => {
        this.pageLoader.open();
        return this.courseService.requestForReview(action.courseId).pipe(
          map((response) => {
            return CourseActions.requestForReviewSuccess({
              courseItem: response,
            });
          }),
          catchError((err) =>
            of(CourseActions.fetchCourseItemsFailed({ error: err }))
          ),
          finalize(() => this.pageLoader.close())
        );
      })
    );
  });

  publishCourse$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CourseActions.publishCourse.type),
      switchMap((action: any) => {
        this.pageLoader.open();
        return this.courseService.requestForReview(action.courseId).pipe(
          map((response) =>
            CourseActions.publishCourseSuccess({ courseItem: response })
          ),
          catchError((err) =>
            of(CourseActions.publishCourseFailed({ error: err }))
          ),
          finalize(() => this.pageLoader.close())
        );
      })
    );
  });
}
